import {
  FREE_TEXT_ANSWER_ROWS,
  FREE_TEXT_NOTE_LABEL,
  OptionGroupDef,
  SPECIALIST_OPTIONS,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";

const NOTE_PLACEHOLDER = "Note (optional)";
const VISIBLE_NOTE_ROWS = 2;
const BODY_PART_PLACEHOLDER = "Body part, e.g. right wrist";
const DESCRIBE_PLACEHOLDER = "Describe";
const LAB_DECLINED = "Patient declined lab work";
const LAB_TESTS = [
  "CBC with Differential",
  "BMP",
  "CMP",
  "Fasting Lipid Panel",
  "Liver Profile",
  "Renal Profile",
  "Fasting Hemoglobin A1C",
  "Urinalysis with Urine Culture & Sensitivity",
  "Urine Albumin Level / Neuropathy Screen",
  "Wound Culture and Sensitivity",
  "Vitamin B12 Blood Level",
  "Hepatitis C",
  "Vitamin D",
  "FOBT - Fecal Occult Blood test",
  "QuantiFERON-TB Blood test",
  "COVID-19 Antibody & Antigen Test",
  "TSH - T3, T4 Level Test",
  "PSA Level (For Men Only)",
];
const IMAGING_DECLINED = "Patient declined Imaging Order";
const IMAGING_TESTS = [
  "Chest Xray - AP/Lat",
  "Xray",
  "MRI",
  "Ultrasound",
  "CT Scan",
  "12 Lead EKG",
  "Echocardiogram",
  "Mammogram",
];

/** "Patient declined" clears every test in the group and vice versa. */
const declinedExclusive = (
  declined: string,
  tests: string[]
): Record<string, string[]> => ({ [declined]: tests });

export const PLAN_OF_CARE_GROUPS: OptionGroupDef[] = [
  {
    key: "planOfCareNarrative",
    title: "Plan of care",
    withDetails: false,
    options: [
      "The patient is being referred to Home Health services for skilled evaluation, management, and treatment of current medical conditions. Skilled nursing and therapy services are medically necessary to assess the patient's condition, monitor response to treatment, and provide ongoing education and care coordination.",
      "All chronic medical conditions are managed by the patient's PCP.",
      "Today's visit involved ongoing management of multiple serious chronic medical conditions requiring longitudinal care, coordination with home health services, medication management, mobility monitoring, fall risk reduction, and functional status assessment. Patient remains at elevated risk for functional decline, injury, and hospitalization requiring continued medical oversight.",
    ],
  },
  {
    key: "planOfTreatment",
    title: "Plan of treatment",
    detailPlaceholder: NOTE_PLACEHOLDER,
    detailsVisibleRows: VISIBLE_NOTE_ROWS,
    optionDetailPlaceholders: {
      Exercise: "e.g. walk 20 minutes daily",
      "Monitor symptoms": "Which symptoms",
    },
    options: [
      "Diet: Diabetic",
      "Diet: Cardiac",
      "Diet: Low Sodium diet",
      "Diet: Low Cholesterol diet",
      "Diet: Low Fat diet",
      "Diet: High Protein diet",
      "Diet: Regular",
      "Diet: Low Sugar",
      "Exercise",
      "Preventive Care: Screenings",
      "Smoking cessation / alcohol counseling",
      "Weight management",
      "Disease-specific education provided",
      "Take medications as prescribed",
      "Monitor symptoms",
      "Fall Prevention",
      "PT eval and Therapy",
    ],
  },
  {
    key: "medicationOrder",
    title: "Medication order",
    withDetails: false,
    freeTextLabel: FREE_TEXT_NOTE_LABEL,
    freeTextRows: FREE_TEXT_ANSWER_ROWS,
    options: ["New Medication"],
  },
  {
    key: "medicationRefill",
    title: "Medication refill",
    chooseOne: true,
    optionDetailPlaceholders: { Refill: "Medication to refill" },
    options: ["Refill", "Patient/pcg not asking for medication refill"],
  },
  {
    key: "changeMedication",
    title: "Change medication",
    optionDetailPlaceholders: {
      "Discontinue Medication": "Which medication",
      "Medication change": "What changes",
    },
    options: ["Discontinue Medication", "Medication change"],
  },
  {
    key: "specialistReferral",
    title: "Specialist referral",
    detailPlaceholder: NOTE_PLACEHOLDER,
    detailsVisibleRows: VISIBLE_NOTE_ROWS,
    options: SPECIALIST_OPTIONS,
  },
  {
    key: "labOrder",
    title: "Lab order",
    withDetails: false,
    options: [...LAB_TESTS, LAB_DECLINED],
    exclusive: declinedExclusive(LAB_DECLINED, LAB_TESTS),
  },
  {
    key: "imagingOrder",
    title: "Imaging order",
    detailPlaceholder: NOTE_PLACEHOLDER,
    optionDetailPlaceholders: {
      Xray: BODY_PART_PLACEHOLDER,
      MRI: BODY_PART_PLACEHOLDER,
      Ultrasound: BODY_PART_PLACEHOLDER,
      "CT Scan": BODY_PART_PLACEHOLDER,
    },
    options: [...IMAGING_TESTS, IMAGING_DECLINED],
    exclusive: declinedExclusive(IMAGING_DECLINED, IMAGING_TESTS),
  },
  {
    key: "incontinenceOrder",
    title: "Incontinence order",
    detailPlaceholder: NOTE_PLACEHOLDER,
    optionDetailPlaceholders: { Diapers: "Size", Others: DESCRIBE_PLACEHOLDER },
    options: [
      "Diapers",
      "Gloves",
      "Liners",
      "Pull Ons",
      "Undergarments",
      "Underpads (Blue Chuck Pads)",
      "Waterproof sheets",
      "Wipes",
      "Others",
    ],
  },
  {
    key: "dmeOrder",
    title: "DME order",
    filter: "Search the DME list",
    detailPlaceholder: NOTE_PLACEHOLDER,
    optionDetailPlaceholders: { Others: DESCRIBE_PLACEHOLDER },
    detailsVisibleRows: VISIBLE_NOTE_ROWS,
    options: [
      "Motorized Wheelchair",
      "Manual Wheelchair",
      "Scooter",
      "Folding Walker",
      "Rollator Walker",
      "Straight Cane",
      "Quad Cane",
      "Underarm Crutches",
      "Forearm Crutches",
      "Seat Lift",
      "Lift Chair",
      "Semi-Electric Adjustable Bed",
      "Full Electric Bed",
      "Foam Mattress Overlay",
      "Pressure Reducing Support Surfaces (Pressure mattress)",
      "Alternating Pressure Pad",
      "Low Air Loss Mattress",
      "Manual Patient's Lift",
      "Bedside Commode",
      "Drop-Arm Commode",
      "Enema Bag",
      "Home blood Glucose Monitor E0607",
      "Blood Glucose Test Strips A4253",
      "Lancets",
      "Diabetic Shoes",
      "Inserts",
      "Nebulizer",
      "CPAP/BiPAP",
      "Tracheostomy Supplies",
      "Surgical Dressing",
      "Negative Pressure Wound Therapy",
      "Ostomy Supplies",
      "Cervical Traction Frame",
      "Speech Generating Devices",
      "Compression Stockings (30-40 mmhg)",
      "Pneumatic Compression Device (Lymphedema pump)",
      "Lumbar Support",
      "Ankle Support",
      "Ankle Foot Orthosis",
      "Knee Support",
      "Knee Ankle Orthosis",
      "Sacral Orthosis",
      "Blood Pressure Monitor",
      "Lightweight wheelchair",
      "Shower Chair",
      "Shower Rails",
      "Seat Cushion",
      "Back Cushion",
      "Rear anti-tippers",
      "Wheel lock extensions",
      "Heel loop",
      "Oxygen Concentrator",
      "Portable Oxygen",
      "Sacral Support",
      "Walking Boots",
      "Heating Pad",
      "Shoulder Strap",
      "Abdominal Binder",
      "Tens Unit",
      "Urinal",
      "Cervical Collar",
      "Bed Pan",
      "Lumbar Orthosis",
      "Others",
    ],
  },
];
